import { parseCookie, stringifySetCookie } from "cookie";
import flatten from "lodash-es/flatten";
import { all, call, delay, put, takeLatest } from "redux-saga/effects";

import {
    STROLLER_ALL_REVIEWS_PHOTOS_CHECK_HIDE_THUMBS,
    STROLLER_ALL_REVIEWS_PHOTOS_FETCH_TRIGGER,
    STROLLER_ALL_REVIEWS_PHOTOS_TOGGLE_THUMBS_TRIGGER,
    StrollerAllReviewsPhotosCheckHideThumbsAction,
    StrollerAllReviewsPhotosFetchNormalizedResponse,
    StrollerAllReviewsPhotosFetchTriggerAction,
    StrollerAllReviewsPhotosToggleThumbsTriggerAction,
    strollerAllReviewsPhotosFetchApi,
    strollerAllReviewsPhotosUpdateThumbs,
} from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.actions";
import { STROLLER_ALL_REVIEWS_PHOTOS_HIDE_THUMBNAILS_COOKIE } from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotosPage";
import {
    XHRAction,
    XHRError,
    XHRGetError,
    handleXHRGetErrorSaga,
    normalizeError,
} from "@/mk2/helpers/api";
import { getLogger } from "@/mk2/logger";
import {
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
    StrollerUserReviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { HistoryAction } from "@/mk2/services/browserHistory";
import { strollers_api_v2_reviews_photos_get_url } from "@/mk/urls/functions";

const logger = getLogger("strollers/StrollerAllReviewsPhotos.sagas");

const getStrollerPhotosHideThumbnailsCookie = (): boolean => {
    if (typeof window !== "undefined") {
        return (
            parseCookie(document.cookie)[STROLLER_ALL_REVIEWS_PHOTOS_HIDE_THUMBNAILS_COOKIE] ===
                String(true) || false
        );
    }
    return false;
};

interface FetchApiResponse {
    body: StrollerAllReviewsPhotosFetchNormalizedResponse;
}

function* fetchStrollerReviews({
    strollerSlug,
    photoId,
    xhr,
    location,
    routeNavigate,
}: StrollerAllReviewsPhotosFetchTriggerAction & XHRAction & HistoryAction) {
    yield put(strollerAllReviewsPhotosFetchApi.request(strollerSlug, photoId));

    try {
        const response: FetchApiResponse = yield call(() =>
            xhr.get(strollers_api_v2_reviews_photos_get_url({ strollerSlug })),
        );

        const reviewPhotos: StrollerUserReviewPhotoEntity[] = flatten(
            getDenormalizedEntities<StrollerUserReviewEntity>(
                response.body,
                StrollerUserReviewSchema,
                response.body.result.strollerReviews,
            ).map((review) => review.photos),
        );
        const reviewPhoto = reviewPhotos.find((p) => p.photoFile.id === photoId);
        if (!reviewPhoto) {
            const err = new Error(
                "Photo not found",
            ) as XHRError<StrollerAllReviewsPhotosFetchNormalizedResponse>;
            err.status = 404;
            err.response = response;
            throw err;
        }

        yield put(strollerAllReviewsPhotosFetchApi.success(strollerSlug, photoId, response.body));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, location, routeNavigate, logger);
        yield put(
            strollerAllReviewsPhotosFetchApi.failure(strollerSlug, photoId, normalizeError(error)),
        );
    }
}

function* toggleThumbs({ xhr }: StrollerAllReviewsPhotosToggleThumbsTriggerAction & XHRAction) {
    const currentValue = getStrollerPhotosHideThumbnailsCookie();

    // when changing showThumbnails then the ImageGalleryLoadable need to be unmounted and re-rendered (to avoid buggy
    // behaviour of showThumbnail) */}
    yield put(strollerAllReviewsPhotosUpdateThumbs(null));
    yield delay(500);

    const newValue = !currentValue;
    document.cookie = stringifySetCookie({
        name: STROLLER_ALL_REVIEWS_PHOTOS_HIDE_THUMBNAILS_COOKIE,
        value: String(newValue),
        path: "/",
    });
    yield put(strollerAllReviewsPhotosUpdateThumbs(newValue));
}

function* checkHideThumbs({ xhr }: StrollerAllReviewsPhotosCheckHideThumbsAction & XHRAction) {
    const currentValue = getStrollerPhotosHideThumbnailsCookie();
    yield put(strollerAllReviewsPhotosUpdateThumbs(currentValue));
}

export default function* root() {
    yield all([
        takeLatest(STROLLER_ALL_REVIEWS_PHOTOS_FETCH_TRIGGER, fetchStrollerReviews),
        takeLatest(STROLLER_ALL_REVIEWS_PHOTOS_TOGGLE_THUMBS_TRIGGER, toggleThumbs),
        takeLatest(STROLLER_ALL_REVIEWS_PHOTOS_CHECK_HIDE_THUMBS, checkHideThumbs),
    ]);
}
